import { Button } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { feeInformationToCreation } from "features/fees/FeeUtils";
import FeeForm from "features/fees/form/FeeForm";
import {
  getFeeInformationListQueryKey,
  useFeeInformationByFeeListSuspense,
} from "gen/endpoints/fee-information/fee-information";
import { useFeeDestroy, useFeeUpdate } from "gen/endpoints/fees/fees";
import { FeeCreateAPIDataRequest } from "gen/model/feeCreateAPIDataRequest";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Restricted } from "shared/components/access/Restricted";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { DeleteConfirmationDialog } from "shared/components/modal/DeleteConfirmationDialog";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const FundFamilySetupFundFeesEditPage: React.FC = () => {
  const { t } = useTranslation(["fundFamily", "fees", "common"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { fundFamilyId, feeId } = useRequiredStringParams();

  const feesPath = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .setup({})
    .fund({})
    .fees({}).$;

  const confirmDelete = useConfirmationDialog(
    DeleteConfirmationDialog as DeleteConfirmationDialog,
    {
      title: t("fees:delete.title"),
      description: t("fees:delete.message"),
      successMessage: t("fees:delete.success"),
      performDelete: () => deleteFee({ fundFamilyId, id: feeId }),
    },
  );

  const { data: feeData } = useFeeInformationByFeeListSuspense(
    fundFamilyId,
    feeId,
    undefined,
    { query: { select: feeInformationToCreation } },
  );

  const { mutateAsync: updateFee } = useFeeUpdate();
  const { mutateAsync: deleteFee } = useFeeDestroy();

  const onDelete = useCallback(async () => {
    const success = await confirmDelete();
    if (success) {
      await queryClient.invalidateQueries({
        queryKey: getFeeInformationListQueryKey(fundFamilyId),
      });
      navigate(feesPath);
    }
  }, [confirmDelete, feesPath, fundFamilyId, navigate, queryClient]);

  const onCancel = useCallback(() => {
    navigate(feesPath);
  }, [feesPath, navigate]);

  return (
    <PageContent contentWidth="boxed">
      <PageHeader
        size="lg"
        title={t("fees:edit.title")}
        backButton={{
          title: t("common:fees"),
          path: rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .setup({})
            .fund({})
            .fees({}).$,
        }}
        actions={
          <Restricted permission={PermissionTypeEnum.config}>
            <Button onClick={onDelete} colorScheme="destructive">
              {t("common:delete")}
            </Button>
          </Restricted>
        }
      />
      <PageBody>
        <FeeForm
          fundFamilyId={fundFamilyId}
          feeData={feeData}
          submitButtonText={t("fees:edit.save")}
          successMessage={t("fees:edit.success")}
          onSubmit={async (data: FeeCreateAPIDataRequest) => {
            await updateFee({ fundFamilyId, id: feeId, data });
          }}
          onSuccess={() => {
            navigate(feesPath);
          }}
          onCancel={onCancel}
        />
      </PageBody>
    </PageContent>
  );
};
